import React, { useState, useEffect } from "react";
import T2TTopNav from "../Layout/T2TTopNav";
import T2TRequestTypesTab from "./T2TRequestTypesTab";
import T2TVendorSignupsTab from "./T2TVendorSignupsTab";
import ScreenGroupsAdmin from "./ScreenGroupsAdmin";
import FieldAccessAdmin from "./FieldAccessAdmin";
import LookupAdmin from "./LookupAdmin";
import VendorRolesAdmin from "./VendorRolesAdmin";
import SlaConfigAdmin from "./SlaConfigAdmin";
import T2THelpButton from "../Guidance/T2THelpButton";
import { getData, postData, putData, deleteData } from "../../../FetchApiServices";
import { useToast, useConfirm } from "../Layout/T2TUIProvider";

const COLORS = ["#36B82A","#2563EB","#D97706","#DC2626","#7C3AED","#0F766E","#1B3A6B","#DB2777"];

// BUG-20 — Channel/provider catalog. Drives the Add/Edit Channel modal.
// Backend `t2t_notification_channels` is keyed by `channel` (1:1) so only one
// row per channel type — picking a type that exists pre-fills its current row.
const CHANNEL_TYPES = [
  { value: "email",    label: "Email",    senderLabel: "From email address",  senderPlaceholder: "noreply@example.com",
    providers: [
      { value: "sendgrid", label: "SendGrid",     fields: ["api_key"] },
      { value: "postmark", label: "Postmark",     fields: ["api_key"] },
      { value: "smtp",     label: "SMTP server",  fields: ["api_key", "api_secret"] },
      { value: "ses",      label: "Amazon SES",   fields: ["api_key", "api_secret", "region"] },
    ] },
  { value: "whatsapp", label: "WhatsApp", senderLabel: "Sender phone / Bot ID", senderPlaceholder: "+91XXXXXXXXXX",
    providers: [
      { value: "wati",     label: "WATI",         fields: ["api_key"] },
      { value: "twilio",   label: "Twilio",       fields: ["api_key", "api_secret"] },
      { value: "gupshup",  label: "Gupshup",      fields: ["api_key"] },
    ] },
  { value: "sms",      label: "SMS",      senderLabel: "Sender ID",            senderPlaceholder: "VENDOR",
    providers: [
      { value: "twilio",   label: "Twilio",       fields: ["api_key", "api_secret"] },
      { value: "msg91",    label: "MSG91",        fields: ["api_key"] },
    ] },
  { value: "in_app",   label: "In-app (bell)", senderLabel: "",                senderPlaceholder: "",
    providers: [
      { value: "native",   label: "Native (built-in)", fields: [] },
    ] },
];
const findChannelDef    = (channelValue) => CHANNEL_TYPES.find((c) => c.value === channelValue) || CHANNEL_TYPES[0];
const findProviderDef   = (channelValue, providerValue) => {
  const def = findChannelDef(channelValue);
  return def.providers.find((p) => p.value === providerValue) || def.providers[0];
};

// Default template events (used when API returns empty)
const DEFAULT_EVENTS = [
  { event_type: "request_submitted", label: "Request Submitted" },
  { event_type: "stage_assigned",    label: "Awaiting Approval" },
  { event_type: "approved",          label: "Request Approved" },
  { event_type: "rejected",          label: "Request Rejected" },
  { event_type: "sla_warning",       label: "SLA Warning" },
  { event_type: "sla_breach",        label: "SLA Breached" },
];

// All permission flags from the spec — grouped for the UI checkbox grid
const PERMISSION_GROUPS = [
  {
    label: "Workflow Builder",
    flags: [
      { key: "workflow_builder.view",    label: "View" },
      { key: "workflow_builder.create",  label: "Create" },
      { key: "workflow_builder.edit",    label: "Edit" },
      { key: "workflow_builder.publish", label: "Publish" },
      { key: "workflow_builder.archive", label: "Archive" },
      { key: "workflow_builder.delete",  label: "Delete" },
      { key: "workflow_builder.clone",   label: "Clone" },
    ],
  },
  {
    label: "Administration",
    flags: [
      { key: "org.manage",            label: "Manage Org Setup" },
      { key: "roles.manage",          label: "Manage Roles" },
      { key: "users.manage",          label: "Manage Users" },
      { key: "request_types.manage",  label: "Manage Request Types" },
      { key: "vendor_portal.manage",  label: "Approve Vendor Sign-ups" },
    ],
  },
  {
    label: "Requests",
    flags: [
      { key: "requests.view_all",  label: "View All Requests" },
      { key: "requests.reassign",  label: "Reassign Requests" },
    ],
  },
  {
    label: "Reports",
    flags: [
      { key: "reports.view",   label: "View Reports" },
      { key: "reports.export", label: "Export Reports" },
    ],
  },
  {
    // User-scope operations — admin bypass does NOT auto-grant these in T2TUIProvider.
    // An admin-only role should leave these off; approver/user roles should grant them.
    label: "Operations (User-scope)",
    flags: [
      { key: "operations.delegate", label: "Delegate My Work (OOO)" },
    ],
  },
];

const DEFAULT_PERMISSIONS = {};
PERMISSION_GROUPS.forEach((g) => g.flags.forEach((f) => { DEFAULT_PERMISSIONS[f.key] = false; }));

const EMPTY_ROLE_FORM = { name: "", color: COLORS[0], description: "", org_unit_id: "", permissions: { ...DEFAULT_PERMISSIONS } };
const EMPTY_USER_FORM = {
  name: "", email: "", password: "", phone: "", t2t_role_id: "", status: 1,
  group_id: "", company_id: "", division_id: "", department_id: "", job_title: "",
  ro_role_id: "", reporting_officer_id: "",
  newPassword: "", confirmPassword: "",
};


const T2TAdmin = () => {
  const toast   = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState("roles");

  // ---- Roles state ----
  const [roles, setRoles]             = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError]   = useState("");
  const [roleModal, setRoleModal]     = useState(null); // null | "new" | role_id
  const [roleForm, setRoleForm]       = useState(EMPTY_ROLE_FORM);
  const [roleSaving, setRoleSaving]   = useState(false);
  const [roleFormError, setRoleFormError] = useState("");

  // ---- Org units (for role scope dropdown) ----
  const [orgUnits, setOrgUnits] = useState([]);

  // ---- Users state ----
  const [users, setUsers]             = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError]   = useState("");
  const [userModal, setUserModal]     = useState(null); // null | "new" | user_id
  const [userForm, setUserForm]       = useState(EMPTY_USER_FORM);
  const [userSaving, setUserSaving]   = useState(false);
  const [userFormError, setUserFormError] = useState("");
  const [showPwdChange, setShowPwdChange] = useState(false);
  const [pwdSaving, setPwdSaving]         = useState(false);
  const [pwdError,  setPwdError]          = useState("");
  const [pwdSuccess, setPwdSuccess]       = useState(false);

  // ---- Bulk import state ----
  const [showImport, setShowImport]         = useState(false);
  const [importFile, setImportFile]         = useState(null);
  const [importPreview, setImportPreview]   = useState([]);
  const [importErrors, setImportErrors]     = useState([]);
  const [importing, setImporting]           = useState(false);
  const [importResult, setImportResult]     = useState(null);

  // ---- Notification templates state ----
  const [notifTemplates, setNotifTemplates] = useState([]);
  const [notifLoading, setNotifLoading]     = useState(false);
  const [notifSaving, setNotifSaving]       = useState(false);
  // BUG-19 — Add Notification modal
  const [notifAddModal, setNotifAddModal]   = useState(false);
  const [notifAddForm, setNotifAddForm]     = useState({ event_type: "", label: "", channel: "in_app" });
  const [notifAddError, setNotifAddError]   = useState("");

  // ---- Channel configs state ----
  const [channels, setChannels]           = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  // BUG-20 — Channel add/edit modal
  const [channelModal, setChannelModal]   = useState(false);
  const [channelForm, setChannelForm]     = useState({ channel: "email", provider: "sendgrid", from_address: "", is_active: true, config: { api_key: "", api_secret: "", region: "" } });
  const [channelFormError, setChannelFormError] = useState("");

  // ---- Integrations state (SAP + Verification) ----
  const [sapConfig, setSapConfig]         = useState({ base_url: "", client_id: "", client_secret: "", company_code: "", is_active: false });
  const [sapSaving, setSapSaving]         = useState(false);
  const [sapTesting, setSapTesting]       = useState(false);
  const [sapTestResult, setSapTestResult] = useState(null);
  const [verifConfigs, setVerifConfigs]   = useState([]);
  const [verifLoading, setVerifLoading]   = useState(false);

  // ---- WATI WhatsApp state ----
  const [watiConfig, setWatiConfig]       = useState({ api_token: "", endpoint_url: "", business_number: "", is_active: false });
  const [watiSaving, setWatiSaving]       = useState(false);
  const [watiTesting, setWatiTesting]     = useState(false);
  const [watiTestResult, setWatiTestResult] = useState(null);
  const [watiTemplates, setWatiTemplates] = useState([]);
  const [watiStats, setWatiStats]         = useState(null);
  const [watiTestMsg, setWatiTestMsg]     = useState("");
  const [watiSendingTest, setWatiSendingTest] = useState(false);

  // ---- SSO/SAML state ----
  const [ssoConfig, setSsoConfig]       = useState({ sso_type: "none", idp_entity_id: "", idp_sso_url: "", idp_certificate: "", sp_entity_id: "", auto_provision: false, fiori_embed_enabled: false, fiori_origin: "", is_active: false });
  const [ssoSaving, setSsoSaving]       = useState(false);
  const [ssoTesting, setSsoTesting]     = useState(false);
  const [ssoTestResult, setSsoTestResult] = useState(null);

  // ---- Ariba state ----
  const [aribaConfig, setAribaConfig]       = useState({ api_key: "", realm: "", network_id: "", is_active: false });
  const [aribaSaving, setAribaSaving]       = useState(false);
  const [aribaTesting, setAribaTesting]     = useState(false);
  const [aribaTestResult, setAribaTestResult] = useState(null);
  const [aribaSyncStatus, setAribaSyncStatus] = useState(null);
  const [aribaSyncing, setAribaSyncing]     = useState(false);

  // ---- Fetch on tab switch ----
  useEffect(() => {
    if (activeTab === "roles") { fetchRoles(); fetchOrgUnits(); }
    if (activeTab === "users") { fetchUsers(); fetchOrgUnits(); }
    if (activeTab === "notifs") { fetchNotifTemplates(); }
    if (activeTab === "channels") { fetchChannels(); }
    if (activeTab === "integrations") { fetchSapConfig(); fetchVerifConfigs(); fetchWatiConfig(); fetchAribaConfig(); fetchSsoConfig(); }
    if (activeTab === "guidance") { fetchGuidanceAnalytics(); }
  }, [activeTab]);

  // ========== ORG UNITS ==========

  const fetchOrgUnits = async () => {
    const resp = await getData("t2t/org-units");
    if (!resp.error) {
      const raw = resp.data?.data;
      setOrgUnits(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    }
  };

  // ========== ROLES ==========

  const fetchRoles = async () => {
    setRolesLoading(true);
    setRolesError("");
    const resp = await getData("t2t/roles");
    if (resp.error) {
      setRolesError(resp.error.message || "Failed to load roles.");
    } else {
      const raw = resp.data?.data;
      setRoles(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    }
    setRolesLoading(false);
  };

  const openRoleModal = (role = null) => {
    setRoleFormError("");
    if (role) {
      setRoleForm({
        name:        role.name,
        color:       role.color || COLORS[0],
        description: role.description || "",
        org_unit_id: role.org_unit_id ?? role.org_unit ?? "",
        permissions: { ...DEFAULT_PERMISSIONS, ...(role.permissions || {}) },
      });
      setRoleModal(role.role_id);
    } else {
      setRoleForm(EMPTY_ROLE_FORM);
      setRoleModal("new");
    }
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) { setRoleFormError("Role name is required."); return; }
    setRoleFormError("");
    setRoleSaving(true);

    const payload = {
      name:        roleForm.name,
      color:       roleForm.color,
      description: roleForm.description,
      permissions: roleForm.permissions,
      ...(roleForm.org_unit_id !== "" && { org_unit_id: Number(roleForm.org_unit_id) }),
    };

    let resp;
    if (roleModal === "new") {
      resp = await postData("t2t/roles", payload);
    } else {
      resp = await putData(`t2t/roles/${roleModal}`, payload);
    }

    if (resp.error) {
      setRoleFormError(resp.error.message || "Failed to save role.");
    } else {
      toast(roleModal === "new" ? "Role created." : "Role updated.", "success");
      setRoleModal(null);
      fetchRoles();
    }
    setRoleSaving(false);
  };

  const deleteRole = async (roleId) => {
    const ok = await confirm("This role will be permanently deleted.", "Delete Role?");
    if (!ok) return;
    const resp = await deleteData(`t2t/roles/${roleId}`);
    if (resp.error) {
      toast(resp.error.message || "Failed to delete role.", "error");
      return;
    }
    toast("Role deleted.", "success");
    fetchRoles();
  };

  // ========== USERS ==========

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError("");
    const resp = await getData("users?is_t2t=true");
    if (resp.error) {
      setUsersError(resp.error.message || "Failed to load users.");
    } else {
      setUsers(resp.data?.data || []);
    }
    setUsersLoading(false);
  };

  const openUserModal = (user = null) => {
    setUserFormError("");
    setShowPwdChange(false);
    setPwdError("");
    setPwdSuccess(false);
    if (user) {
      setUserForm({
        name: user.name, email: user.email, password: "", phone: user.phone || "", t2t_role_id: user.t2t_role_id ?? "", status: user.status ?? 1,
        group_id:      user.t2t_group_id      ?? "",
        company_id:    user.t2t_company_id    ?? "",
        division_id:   user.t2t_division_id   ?? "",
        department_id: user.t2t_department_id ?? "",
        job_title:     user.t2t_job_title     || "",
        ro_role_id: user.ro_role_id ?? "",
        reporting_officer_id: user.reporting_officer_id ?? "",
      });
      setUserModal(user.user_id);
    } else {
      setUserForm(EMPTY_USER_FORM);
      setUserModal("new");
    }
  };

  const saveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) { setUserFormError("Name and email are required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email.trim())) { setUserFormError("Please enter a valid email address."); return; }
    if (userModal === "new" && !userForm.password.trim()) { setUserFormError("Password is required for new users."); return; }
    if (userModal === "new" && userForm.password.length < 8) { setUserFormError("Password must be at least 8 characters."); return; }
    if (userModal === "new" && !/\d/.test(userForm.password)) { setUserFormError("Password must contain at least one number."); return; }
    if (!userForm.group_id)      { setUserFormError("Group is required."); return; }
    if (!userForm.company_id)    { setUserFormError("Company is required."); return; }
    if (!userForm.division_id)   { setUserFormError("Division is required."); return; }
    if (!userForm.department_id) { setUserFormError("Department is required."); return; }
    if (!userForm.job_title.trim()) { setUserFormError("Job Title is required."); return; }
    if (!userForm.phone || !userForm.phone.trim()) { setUserFormError("Mobile number is required."); return; }
    if (!/^\+?[\d\s-]{7,20}$/.test(userForm.phone.trim())) { setUserFormError("Please enter a valid mobile number (7–20 digits)."); return; }
    setUserFormError("");
    setUserSaving(true);

    const orgPayload = {
      t2t_group_id:      Number(userForm.group_id),
      t2t_company_id:    Number(userForm.company_id),
      t2t_division_id:   Number(userForm.division_id),
      t2t_department_id: Number(userForm.department_id),
      t2t_job_title:     userForm.job_title,
      phone:             userForm.phone.trim(),
      ...(userForm.ro_role_id && { ro_role_id: Number(userForm.ro_role_id) }),
      ...(userForm.reporting_officer_id && { reporting_officer_id: Number(userForm.reporting_officer_id) }),
    };

    let resp;
    if (userModal === "new") {
      resp = await postData("users", { name: userForm.name, email: userForm.email, password: userForm.password, t2t_role_id: Number(userForm.t2t_role_id), is_t2t: true, ...orgPayload });
    } else {
      resp = await putData(`users/${userModal}`, { name: userForm.name, email: userForm.email, t2t_role_id: Number(userForm.t2t_role_id), status: Number(userForm.status), ...orgPayload });
    }

    if (resp.error) {
      setUserFormError(resp.error.message || "Failed to save user.");
      setUserSaving(false);
      return;
    }
    toast(userModal === "new" ? "User created." : "User updated.", "success");
    setUserSaving(false);
    setUserModal(null);
    setUserForm(EMPTY_USER_FORM);
    fetchUsers();
  };

  const changePassword = async () => {
    setPwdError("");
    setPwdSuccess(false);
    if (!userForm.newPassword) { setPwdError("Please enter a new password."); return; }
    if (userForm.newPassword.length < 8) { setPwdError("Password must be at least 8 characters."); return; }
    if (!/\d/.test(userForm.newPassword)) { setPwdError("Password must contain at least one number."); return; }
    if (userForm.newPassword !== userForm.confirmPassword) { setPwdError("Passwords do not match."); return; }
    setPwdSaving(true);
    const resp = await putData(`users/${userModal}/password`, { new_password: userForm.newPassword });
    if (resp.error) {
      setPwdError(resp.error.message || "Failed to update password.");
    } else {
      setPwdSuccess(true);
      setUserForm((f) => ({ ...f, newPassword: "", confirmPassword: "" }));
      setShowPwdChange(false);
    }
    setPwdSaving(false);
  };

  const deleteUser = async (userId) => {
    const ok = await confirm("This user will be permanently deleted.", "Delete User?");
    if (!ok) return;
    const resp = await deleteData(`users/${userId}`);
    if (resp.error) {
      toast("Failed to delete user. Please try again.", "error");
      return;
    }
    setUsersError("");
    toast("User deleted.", "success");
    fetchUsers();
  };

  // ========== NOTIFICATION TEMPLATES ==========

  const fetchNotifTemplates = async () => {
    setNotifLoading(true);
    const resp = await getData("t2t/notification-templates");
    if (!resp.error) {
      const raw = resp.data?.data;
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      // If empty, seed from DEFAULT_EVENTS
      if (list.length > 0) {
        setNotifTemplates(list);
      } else {
        setNotifTemplates(DEFAULT_EVENTS.map((e) => ({
          event_type:       e.event_type,
          label:            e.label,
          channel:          "email",
          subject_template: "",
          body_template:    "",
        })));
      }
    }
    setNotifLoading(false);
  };

  const updateNotifField = (idx, field, value) => {
    setNotifTemplates((prev) => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const saveNotifTemplates = async () => {
    setNotifSaving(true);
    const resp = await putData("t2t/notification-templates", { templates: notifTemplates });
    if (resp.error) {
      toast(resp.error.message || "Failed to save templates.", "error");
    } else {
      toast("Notification templates saved.", "success");
    }
    setNotifSaving(false);
  };

  // BUG-19 — Add Notification template (appends to in-memory list; persists on next "Save Templates" click)
  const slugifyEventType = (s) =>
    String(s || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");

  const openNotifAddModal = () => {
    setNotifAddForm({ event_type: "", label: "", channel: "in_app" });
    setNotifAddError("");
    setNotifAddModal(true);
  };

  const submitNotifAddModal = () => {
    const eventType = slugifyEventType(notifAddForm.event_type);
    const label     = notifAddForm.label.trim();
    if (!eventType)   { setNotifAddError("Event type is required."); return; }
    if (!label)       { setNotifAddError("Label is required."); return; }
    if (!/^[a-z][a-z0-9_]*$/.test(eventType)) {
      setNotifAddError("Event type must start with a letter and contain only lowercase letters, numbers and underscores.");
      return;
    }
    if (notifTemplates.some((t) => t.event_type === eventType)) {
      setNotifAddError(`An event type "${eventType}" already exists. Edit it below or pick a different key.`);
      return;
    }
    setNotifTemplates((prev) => [
      ...prev,
      { event_type: eventType, label, channel: notifAddForm.channel, subject_template: "", body_template: "", is_active: 1 },
    ]);
    setNotifAddModal(false);
    toast(`"${label}" added — fill Subject + Body, then Save Templates.`, "success");
  };

  // ========== CHANNEL CONFIGS ==========

  const fetchChannels = async () => {
    setChannelsLoading(true);
    const resp = await getData("t2t/integrations/channels");
    if (!resp.error) {
      const raw = resp.data?.data;
      setChannels(Array.isArray(raw) ? raw : []);
    }
    setChannelsLoading(false);
  };

  const saveChannel = async (channel) => {
    const resp = await postData("t2t/integrations/channels", channel);
    if (resp.error) {
      toast(resp.error.message || "Failed to save channel.", "error");
    } else {
      toast("Channel saved.", "success");
      fetchChannels();
    }
  };

  // BUG-20 — Channel modal helpers
  const openChannelModal = (existing = null) => {
    setChannelFormError("");
    if (existing) {
      // Edit mode — prefill from row. config is JSONB, may be null.
      const cfg = existing.config && typeof existing.config === "object" ? existing.config : {};
      setChannelForm({
        channel:      existing.channel || "email",
        provider:     existing.provider || "sendgrid",
        from_address: existing.from_address || "",
        is_active:    !!existing.is_active,
        config:       { api_key: cfg.api_key || "", api_secret: cfg.api_secret || "", region: cfg.region || "" },
      });
    } else {
      // Add mode — sensible defaults.
      setChannelForm({
        channel: "email", provider: "sendgrid", from_address: "",
        is_active: true,
        config: { api_key: "", api_secret: "", region: "" },
      });
    }
    setChannelModal(true);
  };

  const submitChannelModal = async () => {
    setChannelFormError("");
    const def     = findChannelDef(channelForm.channel);
    const provDef = findProviderDef(channelForm.channel, channelForm.provider);
    // From address required for email/whatsapp/sms (not in_app).
    if (channelForm.channel !== "in_app" && !channelForm.from_address.trim()) {
      setChannelFormError(`${def.senderLabel} is required.`); return;
    }
    // Provider config field requirements.
    for (const f of provDef.fields) {
      if (!String(channelForm.config[f] || "").trim()) {
        const labelMap = { api_key: "API key", api_secret: "API secret", region: "Region" };
        setChannelFormError(`${labelMap[f] || f} is required for ${provDef.label}.`); return;
      }
    }
    // Build payload — strip empty-string config fields so JSONB stays clean.
    const cleanCfg = {};
    for (const f of provDef.fields) {
      const v = String(channelForm.config[f] || "").trim();
      if (v) cleanCfg[f] = v;
    }
    const resp = await postData("t2t/integrations/channels", {
      channel:      channelForm.channel,
      provider:     channelForm.provider,
      from_address: channelForm.from_address.trim(),
      is_active:    !!channelForm.is_active,
      config:       cleanCfg,
    });
    if (resp.error) {
      setChannelFormError(resp.error.message || "Failed to save channel.");
      return;
    }
    toast("Channel saved.", "success");
    setChannelModal(false);
    fetchChannels();
  };

  const disableChannel = async (ch) => {
    // No backend DELETE endpoint — soft-disable via upsert.
    const resp = await postData("t2t/integrations/channels", {
      channel: ch.channel, provider: ch.provider, from_address: ch.from_address,
      is_active: false,
      config: ch.config || {},
    });
    if (resp.error) toast(resp.error.message || "Failed to disable channel.", "error");
    else { toast("Channel disabled.", "success"); fetchChannels(); }
  };

  const testChannel = async (channelId) => {
    const resp = await postData(`t2t/integrations/channels/${channelId}/test`, {});
    if (resp.error) {
      toast(resp.error.message || "Test failed.", "error");
    } else {
      toast("Test notification sent.", "success");
    }
  };

  // ========== SAP CONFIG ==========

  const fetchSapConfig = async () => {
    const resp = await getData("t2t/integrations/sap/config");
    if (!resp.error && resp.data?.data) {
      setSapConfig((prev) => ({ ...prev, ...resp.data.data }));
    }
  };

  const saveSapConfig = async () => {
    setSapSaving(true);
    const resp = await postData("t2t/integrations/sap/config", sapConfig);
    if (resp.error) { toast(resp.error.message || "Failed to save SAP config.", "error"); }
    else { toast("SAP configuration saved.", "success"); }
    setSapSaving(false);
  };

  const testSapConnection = async () => {
    setSapTesting(true);
    setSapTestResult(null);
    const resp = await postData("t2t/integrations/sap/test-connection", {});
    if (resp.error) { setSapTestResult({ ok: false, msg: resp.error.message || "Connection failed." }); }
    else { setSapTestResult({ ok: true, msg: resp.data?.data?.message || "Connection successful!" }); }
    setSapTesting(false);
  };

  // ========== VERIFICATION CONFIG ==========

  const fetchVerifConfigs = async () => {
    setVerifLoading(true);
    const resp = await getData("t2t/integrations/verification/config");
    if (!resp.error) {
      const raw = resp.data?.data;
      setVerifConfigs(Array.isArray(raw) ? raw : []);
    }
    setVerifLoading(false);
  };

  const saveVerifConfig = async (config) => {
    const resp = await postData("t2t/integrations/verification/config", config);
    if (resp.error) { toast(resp.error.message || "Failed to save.", "error"); }
    else { toast("Verification config saved.", "success"); fetchVerifConfigs(); }
  };

  // ========== WATI WHATSAPP CONFIG ==========

  const fetchWatiConfig = async () => {
    const [cfgResp, tplResp, statsResp] = await Promise.all([
      getData("t2t/integrations/wati/config"),
      getData("t2t/integrations/wati/templates"),
      getData("t2t/integrations/wati/delivery-stats"),
    ]);
    if (!cfgResp.error && cfgResp.data?.data) setWatiConfig((prev) => ({ ...prev, ...cfgResp.data.data }));
    if (!tplResp.error) setWatiTemplates(Array.isArray(tplResp.data?.data) ? tplResp.data.data : []);
    if (!statsResp.error && statsResp.data?.data) setWatiStats(statsResp.data.data);
  };

  const saveWatiConfig = async () => {
    setWatiSaving(true);
    const resp = await postData("t2t/integrations/wati/config", watiConfig);
    if (resp.error) { toast(resp.error.message || "Failed to save WATI config.", "error"); }
    else { toast("WATI configuration saved.", "success"); }
    setWatiSaving(false);
  };

  const testWatiConnection = async () => {
    setWatiTesting(true);
    setWatiTestResult(null);
    const resp = await postData("t2t/integrations/wati/test", {});
    if (resp.error) { setWatiTestResult({ ok: false, msg: resp.error.message || "Connection failed." }); }
    else { setWatiTestResult({ ok: true, msg: resp.data?.data?.business_name ? `Connected: ${resp.data.data.business_name}` : "Connection successful!" }); }
    setWatiTesting(false);
  };

  const sendWatiTestMessage = async () => {
    if (!watiTestMsg.trim()) return;
    setWatiSendingTest(true);
    const resp = await postData("t2t/integrations/wati/test-message", { mobile_number: watiTestMsg.trim() });
    if (resp.error) { toast(resp.error.message || "Failed to send test message.", "error"); }
    else { toast("Test WhatsApp sent!", "success"); }
    setWatiSendingTest(false);
  };

  const sendBulkOptIn = async () => {
    const resp = await postData("t2t/users/send-optin-bulk", {});
    if (resp.error) { toast(resp.error.message || "Failed to send opt-in requests.", "error"); }
    else { toast(`Opt-in requests sent to ${resp.data?.data?.count || "all"} users.`, "success"); }
  };

  // ========== ARIBA CONFIG ==========

  const fetchAribaConfig = async () => {
    const [cfgResp, syncResp] = await Promise.all([
      getData("t2t/integrations/ariba/config"),
      getData("t2t/integrations/ariba/sync-status"),
    ]);
    if (!cfgResp.error && cfgResp.data?.data) setAribaConfig((prev) => ({ ...prev, ...cfgResp.data.data }));
    if (!syncResp.error && syncResp.data?.data) setAribaSyncStatus(syncResp.data.data);
  };

  const saveAribaConfig = async () => {
    setAribaSaving(true);
    const resp = await postData("t2t/integrations/ariba/config", aribaConfig);
    if (resp.error) { toast(resp.error.message || "Failed to save Ariba config.", "error"); }
    else { toast("Ariba configuration saved.", "success"); }
    setAribaSaving(false);
  };

  const testAribaConnection = async () => {
    setAribaTesting(true);
    setAribaTestResult(null);
    const resp = await postData("t2t/integrations/ariba/test", {});
    if (resp.error) { setAribaTestResult({ ok: false, msg: resp.error.message || "Connection failed." }); }
    else { setAribaTestResult({ ok: true, msg: resp.data?.data?.message || "Connection successful!" }); }
    setAribaTesting(false);
  };

  const triggerAribaSync = async () => {
    setAribaSyncing(true);
    const resp = await postData("t2t/integrations/ariba/sync-suppliers", {});
    if (resp.error) { toast(resp.error.message || "Sync failed.", "error"); }
    else { toast(`Supplier sync complete. ${resp.data?.data?.count || 0} suppliers synced.`, "success"); fetchAribaConfig(); }
    setAribaSyncing(false);
  };

  // ========== SSO / SAML CONFIG ==========

  const fetchSsoConfig = async () => {
    const resp = await getData("t2t/integrations/sso/config");
    if (!resp.error && resp.data?.data) setSsoConfig((prev) => ({ ...prev, ...resp.data.data }));
  };

  const saveSsoConfig = async () => {
    setSsoSaving(true);
    const resp = await postData("t2t/integrations/sso/config", ssoConfig);
    if (resp.error) { toast(resp.error.message || "Failed to save SSO config.", "error"); }
    else { toast("SSO configuration saved.", "success"); }
    setSsoSaving(false);
  };

  const testSsoConnection = async () => {
    setSsoTesting(true);
    setSsoTestResult(null);
    const resp = await postData("t2t/integrations/sso/test", {});
    if (resp.error) { setSsoTestResult({ ok: false, msg: resp.error.message || "Connection failed." }); }
    else { setSsoTestResult({ ok: true, msg: resp.data?.data?.message || "IdP reachable and certificate valid." }); }
    setSsoTesting(false);
  };

  // ========== GUIDANCE ANALYTICS ==========

  const [guidanceData, setGuidanceData] = useState({ overview: null, dropOff: null, hints: null, templates: null, loading: false });

  const fetchGuidanceAnalytics = async () => {
    setGuidanceData((p) => ({ ...p, loading: true }));
    const [overviewResp, dropOffResp, hintsResp, templatesResp] = await Promise.all([
      getData("t2t/guidance/analytics"),
      getData("t2t/guidance/analytics/drop-off"),
      getData("t2t/guidance/analytics/hint-dismissals"),
      getData("t2t/guidance/analytics/template-usage"),
    ]);
    setGuidanceData({
      overview:  !overviewResp.error  ? (overviewResp.data?.data  || {}) : {},
      dropOff:   !dropOffResp.error   ? (dropOffResp.data?.data   || []) : [],
      hints:     !hintsResp.error     ? (hintsResp.data?.data     || []) : [],
      templates: !templatesResp.error ? (templatesResp.data?.data || []) : [],
      loading: false,
    });
  };

  // ========== BULK IMPORT ==========

  const downloadSampleCsv = () => {
    const headers = "name,email,password,phone,role_name,group,company,division,department,job_title,reporting_manager_email";
    const sample = [
      "Raj Kumar,raj@company.com,Pass@1234,+91 9876543210,Supervisor,OSIF,Jindal,div1,Operation,Senior Analyst,priya@company.com",
      "Priya Mehta,priya@company.com,Pass@5678,+91 9123456789,Manager,OSIF,Jindal,div1,Finance,Finance Controller,",
    ].join("\n");
    const csv = headers + "\n" + sample;
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "t2t_users_sample.csv";
    a.click();
  };

  const handleImportFile = (file) => {
    if (!file) return;
    setImportFile(file);
    setImportErrors([]);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) { setImportErrors(["File is empty or has no data rows."]); return; }

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const required = ["name", "email", "password", "phone"];
      const missing = required.filter((r) => !headers.includes(r));
      if (missing.length > 0) { setImportErrors([`Missing required columns: ${missing.join(", ")}`]); return; }

      const rows = [];
      const errors = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map((c) => c.trim());
        const row = {};
        headers.forEach((h, idx) => { row[h] = cols[idx] || ""; });

        // Validate
        if (!row.name) errors.push(`Row ${i + 1}: Name is empty`);
        if (!row.email) errors.push(`Row ${i + 1}: Email is empty`);
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push(`Row ${i + 1}: Invalid email "${row.email}"`);
        if (!row.password) errors.push(`Row ${i + 1}: Password is empty`);
        else if (row.password.length < 8) errors.push(`Row ${i + 1}: Password must be at least 8 characters`);
        if (!row.phone) errors.push(`Row ${i + 1}: Mobile number is empty`);
        else if (!/^\+?[\d\s-]{7,20}$/.test(row.phone)) errors.push(`Row ${i + 1}: Invalid mobile number "${row.phone}" (7–20 digits)`);
        if (row.reporting_manager_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.reporting_manager_email)) {
          errors.push(`Row ${i + 1}: Invalid reporting manager email "${row.reporting_manager_email}"`);
        }

        rows.push(row);
      }
      setImportPreview(rows);
      setImportErrors(errors);
    };
    reader.readAsText(file);
  };

  const executeImport = async () => {
    if (importPreview.length === 0) return;
    setImporting(true);
    setImportResult(null);

    // Match role names to role IDs
    const roleMap = {};
    roles.forEach((r) => { roleMap[r.name.toLowerCase()] = r.role_id; });

    // Match org unit names to IDs
    const ouByName = {};
    orgUnits.forEach((o) => { ouByName[`${o.type}:${o.name}`.toLowerCase()] = o; });

    // Match user emails → user_id (for reporting manager lookup)
    const userByEmail = {};
    users.forEach((u) => { if (u.email) userByEmail[u.email.toLowerCase()] = u; });

    let success = 0;
    let failed = 0;
    const failedRows = [];

    for (let i = 0; i < importPreview.length; i++) {
      const row = importPreview[i];
      const roleId = roleMap[(row.role_name || "").toLowerCase()] || "";
      const group = orgUnits.find((o) => o.type === "Group" && o.name.toLowerCase() === (row.group || "").toLowerCase());
      const company = orgUnits.find((o) => o.type === "Company" && o.name.toLowerCase() === (row.company || "").toLowerCase() && String(o.parent_id) === String(group?.id));
      const division = orgUnits.find((o) => o.type === "Division" && o.name.toLowerCase() === (row.division || "").toLowerCase() && String(o.parent_id) === String(company?.id));
      const department = orgUnits.find((o) => o.type === "Department" && o.name.toLowerCase() === (row.department || "").toLowerCase() && String(o.parent_id) === String(division?.id));

      // Reporting manager lookup by email (optional)
      const rmEmail = (row.reporting_manager_email || "").toLowerCase().trim();
      const rmUser = rmEmail ? userByEmail[rmEmail] : null;
      if (rmEmail && !rmUser) {
        failed++;
        failedRows.push({ row: i + 2, name: row.name, error: `Reporting manager email "${row.reporting_manager_email}" not found in users` });
        continue;
      }

      const payload = {
        name: row.name,
        email: row.email,
        password: row.password,
        phone: row.phone,
        is_t2t: true,
        t2t_job_title: row.job_title || "",
      };
      if (roleId)     payload.t2t_role_id       = Number(roleId);
      if (group)      payload.t2t_group_id      = Number(group.id);
      if (company)    payload.t2t_company_id    = Number(company.id);
      if (division)   payload.t2t_division_id   = Number(division.id);
      if (department) payload.t2t_department_id = Number(department.id);
      if (rmUser) {
        payload.reporting_officer_id = Number(rmUser.user_id);
        if (rmUser.t2t_role_id) payload.ro_role_id = Number(rmUser.t2t_role_id);
      }

      const resp = await postData("users", payload);
      if (resp.error) {
        failed++;
        failedRows.push({ row: i + 2, name: row.name, error: resp.error.message || "Failed" });
      } else {
        success++;
      }
    }

    setImportResult({ success, failed, failedRows });
    setImporting(false);
    if (success > 0) fetchUsers();
  };

  // ========== RENDER ==========

  return (
    <>
      <T2TTopNav title="Admin" />
      <div className="t2t-content">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="t2t-page-title">Admin</div>
          {/* BUG-21: per-tab contextual help — auto-switches content as activeTab changes */}
          <T2THelpButton routeKey={`admin.${activeTab}`} label={`Help for ${activeTab.replace(/_/g, " ")}`} />
        </div>
        <p className="t2t-page-sub">Manage roles, users and notification templates.</p>

        <div className="t2t-tab-group" style={{ marginBottom: 20 }}>
          {["roles","users","vendor_roles","request_types","vendor_signups","screen_groups","field_access","lookups","sla_config","notifs","channels","integrations","guidance"].map((tab) => (
            <button key={tab} className={`t2t-tab${activeTab === tab ? " active" : ""}`} onClick={() => setActiveTab(tab)}>
              {{ roles: "Roles", users: "Users", vendor_roles: "Vendor Role Assignments", request_types: "Request Types", vendor_signups: "Vendor Sign-ups", screen_groups: "Screen Groups", field_access: "Field Access", lookups: "Lookup Values", sla_config: "SLA Config", notifs: "Notifications", channels: "Channels", integrations: "Integrations", guidance: "Guidance Analytics" }[tab]}
            </button>
          ))}
        </div>

        {/* ---- VENDOR ROLE ASSIGNMENTS (Gap-3) ---- */}
        {activeTab === "vendor_roles" && <VendorRolesAdmin />}

        {/* ---- REQUEST TYPES (fix #2a) ---- */}
        {activeTab === "request_types" && <T2TRequestTypesTab />}

        {/* ---- VENDOR SIGN-UPS (fix #3 / ARCH-03) ---- */}
        {activeTab === "vendor_signups" && <T2TVendorSignupsTab />}

        {/* ---- SCREEN GROUPS (T2T-03-SG1) ---- */}
        {activeTab === "screen_groups" && <ScreenGroupsAdmin />}

        {/* ---- FIELD ACCESS MATRIX (T2T-03-SG2) ---- */}
        {activeTab === "field_access" && <FieldAccessAdmin />}

        {/* ---- LOOKUP VALUES (Step 10) ---- */}
        {activeTab === "lookups" && <LookupAdmin />}

        {/* ---- SLA CONFIG (Step 10) ---- */}
        {activeTab === "sla_config" && <SlaConfigAdmin />}

        {/* ---- ROLES ---- */}
        {activeTab === "roles" && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={() => openRoleModal()}>+ Add Role</button>
            </div>
            {rolesLoading && <div style={{ textAlign: "center", padding: 24, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading roles...</div>}
            {rolesError  && <div style={{ padding: 12, color: "var(--t2t-danger)", fontSize: 13, background: "#FEF2F2", borderRadius: 6, marginBottom: 12 }}>{rolesError}</div>}
            {!rolesLoading && !rolesError && (
              <div className="t2t-admin-table">
                {roles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 13 }}>No roles found.</div>
                ) : (
                  roles.map((role) => (
                    <div className="t2t-admin-row" key={role.role_id}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: role.color, flexShrink: 0 }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{role.name}</div>
                        <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)" }}>
                          {role.description}
                          {role.org_unit_name
                            ? <span> &bull; Scope: {role.org_unit_name}</span>
                            : <span> &bull; Scope: All Org Units</span>
                          }
                        </div>
                      </div>
                      <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => openRoleModal(role)}>Edit</button>
                      <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "var(--t2t-danger)" }} onClick={() => deleteRole(role.role_id)}>Delete</button>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* ---- USERS ---- */}
        {activeTab === "users" && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
              <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => { setShowImport(true); setImportFile(null); setImportPreview([]); setImportErrors([]); setImportResult(null); fetchRoles(); fetchOrgUnits(); }}>
                ↑ Import CSV
              </button>
              <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={() => openUserModal()}>+ Add User</button>
            </div>
            {usersLoading && <div style={{ textAlign: "center", padding: 24, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading users...</div>}
            {usersError  && <div style={{ padding: 12, color: "var(--t2t-danger)", fontSize: 13, background: "#FEF2F2", borderRadius: 6, marginBottom: 12 }}>{usersError}</div>}
            {!usersLoading && !usersError && (
              <div className="t2t-admin-table">
                {users.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 13 }}>No users found.</div>
                ) : (
                  users.map((user) => (
                    <div className="t2t-admin-row" key={user.user_id}>
                      <div className="t2t-avatar" style={{ width: 34, height: 34, fontSize: 12 }}>
                        {user.name?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{user.name}</div>
                        <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)" }}>
                          {user.email}
                          {user.status === 0 && <span style={{ marginLeft: 6, color: "var(--t2t-danger)" }}>• Inactive</span>}
                        </div>
                      </div>
                      {user.t2t_role_name && (
                        <span className="t2t-chip t2t-chip-green" style={{ background: (user.t2t_role_color || "#94A3B8") + "22", color: user.t2t_role_color || "#94A3B8" }}>
                          {user.t2t_role_name}
                        </span>
                      )}
                      <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => openUserModal(user)}>Edit</button>
                      <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "var(--t2t-danger)" }} onClick={() => deleteUser(user.user_id)}>Delete</button>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* ---- NOTIFICATIONS ---- */}
        {activeTab === "notifs" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 12 }}>
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", margin: 0 }}>
                Configure notification templates per event. Use <code style={{ background: "var(--t2t-bg)", padding: "1px 5px", borderRadius: 4 }}>{"{{variable}}"}</code> placeholders.
              </p>
              {!notifLoading && (
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={openNotifAddModal} style={{ flexShrink: 0 }}>
                  + Add Notification
                </button>
              )}
            </div>
            {notifLoading && <div style={{ textAlign: "center", padding: 24, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading templates...</div>}
            {!notifLoading && notifTemplates.map((tpl, i) => {
              const channelPillBg = {
                in_app:   "#EFF6FF",
                email:    "#F0FDF4",
                whatsapp: "#FEF3C7",
                sms:      "#FCE7F3",
              }[tpl.channel || "in_app"] || "#F1F5F9";
              const channelPillFg = {
                in_app:   "#1D4ED8",
                email:    "#16A34A",
                whatsapp: "#92400E",
                sms:      "#9D174D",
              }[tpl.channel || "in_app"] || "#475569";
              return (
                <div key={tpl.event_type || i} style={{ background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius-lg)", padding: 18, marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      📧 {tpl.label || tpl.event_type}
                      <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)", marginLeft: 8, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                        {tpl.event_type}
                      </span>
                    </div>
                    {/* BUG-19: per-card channel pill — click to change */}
                    <select
                      value={tpl.channel || "in_app"}
                      onChange={(e) => updateNotifField(i, "channel", e.target.value)}
                      style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        padding: "3px 8px", borderRadius: 999,
                        background: channelPillBg,
                        color: channelPillFg,
                        border: `1px solid ${channelPillFg}33`,
                        cursor: "pointer",
                      }}
                      title="Change delivery channel"
                    >
                      <option value="in_app">IN-APP</option>
                      <option value="email">EMAIL</option>
                      <option value="whatsapp">WHATSAPP</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                  <div className="t2t-fg">
                    <label>Subject</label>
                    <input className="t2t-fc" value={tpl.subject_template || ""} placeholder="e.g. Request {{ref}} needs your review"
                      onChange={(e) => updateNotifField(i, "subject_template", e.target.value)} />
                  </div>
                  <div className="t2t-fg">
                    <label>Body</label>
                    <textarea className="t2t-fc" rows="3" value={tpl.body_template || ""} placeholder="Full notification body with {{variables}}..."
                      onChange={(e) => updateNotifField(i, "body_template", e.target.value)} />
                  </div>
                </div>
              );
            })}
            {!notifLoading && (
              <button className="t2t-btn t2t-btn-primary" onClick={saveNotifTemplates} disabled={notifSaving}>
                {notifSaving ? "Saving..." : "Save Templates"}
              </button>
            )}
          </>
        )}

        {/* BUG-19 — Add Notification modal */}
        {notifAddModal && (
          <div className="t2t-overlay" onClick={() => setNotifAddModal(false)}>
            <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <div className="t2t-modal-head">
                <div className="t2t-modal-title">Add Notification Template</div>
                <button className="t2t-close-btn" onClick={() => setNotifAddModal(false)}>×</button>
              </div>
              <div className="t2t-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {notifAddError && (
                  <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12 }}>
                    {notifAddError}
                  </div>
                )}
                <div className="t2t-fg">
                  <label>Label *</label>
                  <input
                    className="t2t-fc"
                    placeholder="e.g. Vendor Action Required"
                    value={notifAddForm.label}
                    onChange={(e) => {
                      const newLabel = e.target.value;
                      // Smart auto-sync event_type from label, only while pristine (BUG-09/11 pattern)
                      const prevSlug = slugifyEventType(notifAddForm.label);
                      const eventTypeIsPristine = !notifAddForm.event_type || notifAddForm.event_type === prevSlug;
                      setNotifAddForm(eventTypeIsPristine
                        ? { ...notifAddForm, label: newLabel, event_type: slugifyEventType(newLabel) }
                        : { ...notifAddForm, label: newLabel });
                    }}
                  />
                </div>
                <div className="t2t-fg">
                  <label>Event Type *</label>
                  <input
                    className="t2t-fc"
                    placeholder="e.g. vendor_action_required"
                    value={notifAddForm.event_type}
                    onChange={(e) => setNotifAddForm({ ...notifAddForm, event_type: slugifyEventType(e.target.value) })}
                    style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}
                  />
                  <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 4 }}>
                    Snake_case slug. Used as the unique key — cannot be changed after save.
                  </div>
                </div>
                <div className="t2t-fg">
                  <label>Channel *</label>
                  <select className="t2t-fc" value={notifAddForm.channel} onChange={(e) => setNotifAddForm({ ...notifAddForm, channel: e.target.value })}>
                    <option value="in_app">In-App (bell)</option>
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>
              </div>
              <div className="t2t-modal-foot">
                <button className="t2t-btn t2t-btn-ghost" onClick={() => setNotifAddModal(false)}>Cancel</button>
                <button className="t2t-btn t2t-btn-primary" onClick={submitNotifAddModal}>Add Template</button>
              </div>
            </div>
          </div>
        )}

        {/* ---- CHANNELS ---- */}
        {activeTab === "channels" && (
          <>
            <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>
              Configure notification channels. Active channels receive all workflow notifications.
            </p>
            {channelsLoading && <div style={{ textAlign: "center", padding: 24, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading channels...</div>}
            {!channelsLoading && channels.length === 0 && (
              <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 13 }}>No channels configured yet.</div>
            )}
            {!channelsLoading && channels.map((ch) => (
              <div key={ch.id} style={{ background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius-lg)", padding: 18, marginBottom: 12, display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontSize: 24 }}>
                  {ch.channel === "email" ? "📧" : ch.channel === "whatsapp" ? "💬" : ch.channel === "sms" ? "📱" : "🔔"}
                </span>
                <div
                  style={{ flex: 1, cursor: "pointer" }}
                  onClick={() => openChannelModal(ch)}
                  title="Click to edit channel"
                >
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{ch.channel?.toUpperCase()} — {ch.provider}</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    {ch.from_address && `From: ${ch.from_address}`}
                    {ch.is_active
                      ? <span style={{ marginLeft: 8, color: "#16A34A", fontWeight: 600 }}>Active</span>
                      : <span style={{ marginLeft: 8, color: "#94A3B8", fontWeight: 600 }}>Inactive</span>
                    }
                  </div>
                </div>
                <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 11 }} onClick={() => openChannelModal(ch)}>
                  ✎ Edit
                </button>
                <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ fontSize: 11 }} onClick={() => testChannel(ch.id)}>
                  Test
                </button>
                {ch.is_active && (
                  <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 11, color: "var(--t2t-danger)" }} onClick={() => disableChannel(ch)}>
                    Disable
                  </button>
                )}
              </div>
            ))}
            {!channelsLoading && (
              <button
                className="t2t-btn t2t-btn-primary t2t-btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => openChannelModal()}
              >
                + Add Channel
              </button>
            )}
          </>
        )}

        {/* BUG-20 — Channel add/edit modal */}
        {channelModal && (() => {
          const def     = findChannelDef(channelForm.channel);
          const provDef = findProviderDef(channelForm.channel, channelForm.provider);
          return (
            <div className="t2t-overlay" onClick={() => setChannelModal(false)}>
              <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                <div className="t2t-modal-head">
                  <div className="t2t-modal-title">{channels.some((c) => c.channel === channelForm.channel) ? "Edit Channel" : "Add Channel"}</div>
                  <button className="t2t-close-btn" onClick={() => setChannelModal(false)}>×</button>
                </div>
                <div className="t2t-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {channelFormError && (
                    <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12 }}>
                      {channelFormError}
                    </div>
                  )}
                  <div className="t2t-fg">
                    <label>Channel type *</label>
                    <select
                      className="t2t-fc"
                      value={channelForm.channel}
                      onChange={(e) => {
                        const newType = e.target.value;
                        const newDef  = findChannelDef(newType);
                        // Reset provider to first option of the new type. Prefill from existing row if there is one.
                        const existing = channels.find((c) => c.channel === newType);
                        const cfg = existing?.config && typeof existing.config === "object" ? existing.config : {};
                        setChannelForm({
                          channel: newType,
                          provider: existing?.provider || newDef.providers[0].value,
                          from_address: existing?.from_address || "",
                          is_active: existing ? !!existing.is_active : true,
                          config: { api_key: cfg.api_key || "", api_secret: cfg.api_secret || "", region: cfg.region || "" },
                        });
                      }}
                    >
                      {CHANNEL_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    {channels.some((c) => c.channel === channelForm.channel) && (
                      <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 4 }}>
                        A {def.label} channel already exists — saving will update it.
                      </div>
                    )}
                  </div>
                  <div className="t2t-fg">
                    <label>Provider *</label>
                    <select
                      className="t2t-fc"
                      value={channelForm.provider}
                      onChange={(e) => setChannelForm({ ...channelForm, provider: e.target.value })}
                    >
                      {def.providers.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>

                  {/* From address — hidden for in_app (no sender concept) */}
                  {channelForm.channel !== "in_app" && (
                    <div className="t2t-fg">
                      <label>{def.senderLabel} *</label>
                      <input
                        className="t2t-fc"
                        placeholder={def.senderPlaceholder}
                        value={channelForm.from_address}
                        onChange={(e) => setChannelForm({ ...channelForm, from_address: e.target.value })}
                      />
                    </div>
                  )}

                  {/* Provider-specific config fields */}
                  {provDef.fields.includes("api_key") && (
                    <div className="t2t-fg">
                      <label>API Key *</label>
                      <input
                        className="t2t-fc"
                        type="password"
                        placeholder="Paste provider API key"
                        value={channelForm.config.api_key}
                        onChange={(e) => setChannelForm({ ...channelForm, config: { ...channelForm.config, api_key: e.target.value } })}
                      />
                    </div>
                  )}
                  {provDef.fields.includes("api_secret") && (
                    <div className="t2t-fg">
                      <label>API Secret *</label>
                      <input
                        className="t2t-fc"
                        type="password"
                        placeholder="Paste provider API secret"
                        value={channelForm.config.api_secret}
                        onChange={(e) => setChannelForm({ ...channelForm, config: { ...channelForm.config, api_secret: e.target.value } })}
                      />
                    </div>
                  )}
                  {provDef.fields.includes("region") && (
                    <div className="t2t-fg">
                      <label>Region *</label>
                      <input
                        className="t2t-fc"
                        placeholder="e.g. ap-south-1"
                        value={channelForm.config.region}
                        onChange={(e) => setChannelForm({ ...channelForm, config: { ...channelForm.config, region: e.target.value } })}
                      />
                    </div>
                  )}

                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginTop: 4 }}>
                    <input
                      type="checkbox"
                      checked={!!channelForm.is_active}
                      onChange={(e) => setChannelForm({ ...channelForm, is_active: e.target.checked })}
                    />
                    Active (channel will receive workflow notifications)
                  </label>
                </div>
                <div className="t2t-modal-foot">
                  <button className="t2t-btn t2t-btn-ghost" onClick={() => setChannelModal(false)}>Cancel</button>
                  <button className="t2t-btn t2t-btn-primary" onClick={submitChannelModal}>Save Channel</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ---- INTEGRATIONS ---- */}
        {activeTab === "integrations" && (
          <>
            {/* WATI WhatsApp Config */}
            <div className="t2t-card" style={{ padding: 18, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>💬</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>WhatsApp Notifications (WATI)</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    {watiConfig.is_active
                      ? <span style={{ color: "#25D366", fontWeight: 600 }}>Active</span>
                      : <span style={{ color: "#94A3B8" }}>Not configured</span>}
                  </div>
                </div>
              </div>

              <div className="t2t-fg">
                <label>WATI API Access Token</label>
                <input className="t2t-fc" type="password" value={watiConfig.api_token || ""} placeholder="Bearer token from WATI dashboard"
                  onChange={(e) => setWatiConfig({ ...watiConfig, api_token: e.target.value })} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div className="t2t-fg" style={{ flex: 1 }}>
                  <label>API Endpoint URL</label>
                  <input className="t2t-fc" value={watiConfig.endpoint_url || ""} placeholder="https://live-server.wati.io"
                    onChange={(e) => setWatiConfig({ ...watiConfig, endpoint_url: e.target.value })} />
                </div>
                <div className="t2t-fg" style={{ flex: 1 }}>
                  <label>Business WhatsApp Number</label>
                  <input className="t2t-fc" value={watiConfig.business_number || ""} placeholder="+91XXXXXXXXXX"
                    onChange={(e) => setWatiConfig({ ...watiConfig, business_number: e.target.value })} />
                </div>
              </div>
              <div className="t2t-fg">
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={!!watiConfig.is_active}
                    onChange={(e) => setWatiConfig({ ...watiConfig, is_active: e.target.checked })} />
                  Active (enable WhatsApp notifications)
                </label>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={saveWatiConfig} disabled={watiSaving}>
                  {watiSaving ? "Saving..." : "Save Config"}
                </button>
                <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={testWatiConnection} disabled={watiTesting}>
                  {watiTesting ? "Testing..." : "Test Connection"}
                </button>
                <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={sendBulkOptIn}>Send Opt-In to All Users</button>
              </div>
              {watiTestResult && (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                  background: watiTestResult.ok ? "#F0FDF4" : "#FEF2F2",
                  color: watiTestResult.ok ? "#166534" : "#DC2626",
                  border: `1px solid ${watiTestResult.ok ? "#BBF7D0" : "#FECACA"}` }}>
                  {watiTestResult.ok ? "✅ " : "❌ "}{watiTestResult.msg}
                </div>
              )}

              {/* Send Test Message */}
              <div style={{ marginTop: 14, padding: "12px 14px", background: "#F8FAFC", borderRadius: 8, border: "1px solid var(--t2t-border)" }}>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Send Test Message</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="t2t-fc" value={watiTestMsg} onChange={(e) => setWatiTestMsg(e.target.value)} placeholder="Mobile number (e.g. 919876543210)" style={{ flex: 1 }} />
                  <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={sendWatiTestMessage} disabled={watiSendingTest || !watiTestMsg.trim()}>
                    {watiSendingTest ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>

              {/* Template Status */}
              {watiTemplates.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>WhatsApp Templates</div>
                  {watiTemplates.map((tpl) => {
                    const sColor = tpl.status === "approved" ? "#16A34A" : tpl.status === "rejected" ? "#DC2626" : "#D97706";
                    return (
                      <div key={tpl.id || tpl.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--t2t-border)", marginBottom: 4, fontSize: 12 }}>
                        <span style={{ flex: 1, fontWeight: 600 }}>{tpl.name}</span>
                        <span style={{ fontSize: 10, color: "#64748B" }}>{tpl.category || ""}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: sColor, background: sColor + "18", padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>{tpl.status}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Delivery Stats */}
              {watiStats && (
                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {[
                    { label: "Sent", value: watiStats.total_sent || 0, color: "#2563EB" },
                    { label: "Delivered", value: watiStats.delivered || 0, color: "#16A34A" },
                    { label: "Read", value: watiStats.read || 0, color: "#7C3AED" },
                    { label: "Failed", value: watiStats.failed || 0, color: "#DC2626" },
                    { label: "Opted In", value: watiStats.opt_in_count || 0, color: "#25D366" },
                    { label: "Opted Out", value: watiStats.opt_out_count || 0, color: "#94A3B8" },
                  ].map((s) => (
                    <div key={s.label} style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 6, padding: "8px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ariba Config */}
            <div className="t2t-card" style={{ padding: 18, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🛒</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>SAP Ariba</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    {aribaConfig.is_active
                      ? <span style={{ color: "#F0AB00", fontWeight: 600 }}>Active</span>
                      : <span style={{ color: "#94A3B8" }}>Not configured</span>}
                  </div>
                </div>
              </div>
              <div className="t2t-fg">
                <label>Ariba API Key</label>
                <input className="t2t-fc" type="password" value={aribaConfig.api_key || ""} placeholder="API key from Ariba Developer Portal"
                  onChange={(e) => setAribaConfig({ ...aribaConfig, api_key: e.target.value })} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div className="t2t-fg" style={{ flex: 1 }}>
                  <label>Realm</label>
                  <input className="t2t-fc" value={aribaConfig.realm || ""} placeholder="e.g. mycompany-T"
                    onChange={(e) => setAribaConfig({ ...aribaConfig, realm: e.target.value })} />
                </div>
                <div className="t2t-fg" style={{ flex: 1 }}>
                  <label>Ariba Network ID (ANID)</label>
                  <input className="t2t-fc" value={aribaConfig.network_id || ""} placeholder="AN01234567890"
                    onChange={(e) => setAribaConfig({ ...aribaConfig, network_id: e.target.value })} />
                </div>
              </div>
              <div className="t2t-fg">
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={!!aribaConfig.is_active}
                    onChange={(e) => setAribaConfig({ ...aribaConfig, is_active: e.target.checked })} />
                  Active (enable Ariba supplier sync + PO transmission)
                </label>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={saveAribaConfig} disabled={aribaSaving}>
                  {aribaSaving ? "Saving..." : "Save Config"}
                </button>
                <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={testAribaConnection} disabled={aribaTesting}>
                  {aribaTesting ? "Testing..." : "Test Connection"}
                </button>
                <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={triggerAribaSync} disabled={aribaSyncing}>
                  {aribaSyncing ? "Syncing..." : "Sync Suppliers Now"}
                </button>
              </div>
              {aribaTestResult && (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                  background: aribaTestResult.ok ? "#F0FDF4" : "#FEF2F2",
                  color: aribaTestResult.ok ? "#166534" : "#DC2626",
                  border: `1px solid ${aribaTestResult.ok ? "#BBF7D0" : "#FECACA"}` }}>
                  {aribaTestResult.ok ? "✅ " : "❌ "}{aribaTestResult.msg}
                </div>
              )}
              {aribaSyncStatus && (
                <div style={{ marginTop: 10, padding: "8px 12px", background: "#F8FAFC", borderRadius: 6, fontSize: 11, color: "var(--t2t-text-secondary)" }}>
                  Suppliers synced: <strong>{aribaSyncStatus.supplier_count || 0}</strong>
                  {aribaSyncStatus.last_synced_at && <span> &middot; Last sync: {new Date(aribaSyncStatus.last_synced_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                </div>
              )}
            </div>

            {/* SAP S/4HANA Config */}
            <div className="t2t-card" style={{ padding: 18, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🔗</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>SAP S/4HANA</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    {sapConfig.is_active
                      ? <span style={{ color: "#16A34A", fontWeight: 600 }}>Active</span>
                      : <span style={{ color: "#94A3B8" }}>Not configured</span>
                    }
                  </div>
                </div>
              </div>
              <div className="t2t-fg">
                <label>SAP Base URL</label>
                <input className="t2t-fc" value={sapConfig.base_url || ""} placeholder="https://sap-server.company.com:8443/sap/opu/odata"
                  onChange={(e) => setSapConfig({ ...sapConfig, base_url: e.target.value })} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div className="t2t-fg" style={{ flex: 1 }}>
                  <label>Client ID</label>
                  <input className="t2t-fc" value={sapConfig.client_id || ""} placeholder="SAP client ID"
                    onChange={(e) => setSapConfig({ ...sapConfig, client_id: e.target.value })} />
                </div>
                <div className="t2t-fg" style={{ flex: 1 }}>
                  <label>Client Secret</label>
                  <input className="t2t-fc" type="password" value={sapConfig.client_secret || ""} placeholder="••••••••"
                    onChange={(e) => setSapConfig({ ...sapConfig, client_secret: e.target.value })} />
                </div>
              </div>
              <div className="t2t-fg">
                <label>Company Code</label>
                <input className="t2t-fc" value={sapConfig.company_code || ""} placeholder="e.g. 1000"
                  onChange={(e) => setSapConfig({ ...sapConfig, company_code: e.target.value })} />
              </div>
              <div className="t2t-fg">
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={!!sapConfig.is_active}
                    onChange={(e) => setSapConfig({ ...sapConfig, is_active: e.target.checked })} />
                  Active (enable SAP push stages)
                </label>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={saveSapConfig} disabled={sapSaving}>
                  {sapSaving ? "Saving..." : "Save Config"}
                </button>
                <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={testSapConnection} disabled={sapTesting}>
                  {sapTesting ? "Testing..." : "Test Connection"}
                </button>
              </div>
              {sapTestResult && (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                  background: sapTestResult.ok ? "#F0FDF4" : "#FEF2F2",
                  color: sapTestResult.ok ? "#166534" : "#DC2626",
                  border: `1px solid ${sapTestResult.ok ? "#BBF7D0" : "#FECACA"}` }}>
                  {sapTestResult.ok ? "✅ " : "❌ "}{sapTestResult.msg}
                </div>
              )}
            </div>

            {/* Verification API Providers */}
            <div className="t2t-card" style={{ padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🔍</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Verification APIs</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>PAN, GST, and Bank Account verification providers</div>
                </div>
              </div>

              {verifLoading && <div style={{ textAlign: "center", padding: 16, color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>}

              {[
                { type: "pan",          label: "PAN Verification",          icon: "🪪", provider: "surepass",  fields: ["api_key"] },
                { type: "gst",          label: "GST Verification",          icon: "📋", provider: "masters_india", fields: ["api_key"] },
                { type: "bank_account", label: "Bank Account (Penny Drop)", icon: "🏦", provider: "cashfree",  fields: ["client_id", "client_secret"] },
              ].map((v) => {
                const existing = verifConfigs.find((c) => c.verification_type === v.type);
                return (
                  <div key={v.type} style={{ border: "1px solid var(--t2t-border)", borderRadius: 8, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span>{v.icon}</span>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{v.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600, color: existing?.is_active ? "#16A34A" : "#94A3B8" }}>
                        {existing?.is_active ? "Active" : "Not configured"}
                      </span>
                    </div>
                    {v.fields.map((f) => (
                      <div className="t2t-fg" key={f}>
                        <label>{f === "api_key" ? "API Key" : f === "client_id" ? "Client ID" : "Client Secret"}</label>
                        <input
                          className="t2t-fc" type="password"
                          defaultValue={existing?.[f] || ""}
                          placeholder="••••••••"
                          id={`verif-${v.type}-${f}`}
                        />
                      </div>
                    ))}
                    <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ fontSize: 11 }}
                      onClick={() => {
                        const payload = { verification_type: v.type, provider: v.provider, is_active: true };
                        v.fields.forEach((f) => { payload[f] = document.getElementById(`verif-${v.type}-${f}`)?.value || ""; });
                        saveVerifConfig(payload);
                      }}>
                      Save {v.label}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* SSO / SAML Config (INT-12) */}
            <div className="t2t-card" style={{ padding: 18, marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🔐</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Single Sign-On (SAP Fiori / SAML 2.0)</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    {ssoConfig.is_active
                      ? <span style={{ color: "#1B3A6B", fontWeight: 600 }}>Active</span>
                      : <span style={{ color: "#94A3B8" }}>Not configured</span>}
                  </div>
                </div>
              </div>
              <div className="t2t-fg">
                <label>SSO Type</label>
                <select className="t2t-fc" value={ssoConfig.sso_type} onChange={(e) => setSsoConfig({ ...ssoConfig, sso_type: e.target.value })}>
                  <option value="none">None (disabled)</option>
                  <option value="saml2">SAML 2.0 (SAP IAS)</option>
                  <option value="oauth2">OAuth 2.0</option>
                </select>
              </div>
              {ssoConfig.sso_type !== "none" && (
                <>
                  <div style={{ display: "flex", gap: 10 }}>
                    <div className="t2t-fg" style={{ flex: 1 }}>
                      <label>IdP Entity ID</label>
                      <input className="t2t-fc" value={ssoConfig.idp_entity_id || ""} placeholder="SAP IAS Entity ID"
                        onChange={(e) => setSsoConfig({ ...ssoConfig, idp_entity_id: e.target.value })} />
                    </div>
                    <div className="t2t-fg" style={{ flex: 1 }}>
                      <label>SP Entity ID</label>
                      <input className="t2t-fc" value={ssoConfig.sp_entity_id || ""} placeholder="osif-sp-entity-id"
                        onChange={(e) => setSsoConfig({ ...ssoConfig, sp_entity_id: e.target.value })} />
                    </div>
                  </div>
                  <div className="t2t-fg">
                    <label>IdP SSO URL</label>
                    <input className="t2t-fc" value={ssoConfig.idp_sso_url || ""} placeholder="https://your-tenant.accounts.ondemand.com/saml2/idp/sso"
                      onChange={(e) => setSsoConfig({ ...ssoConfig, idp_sso_url: e.target.value })} />
                  </div>
                  <div className="t2t-fg">
                    <label>IdP X.509 Certificate</label>
                    <textarea className="t2t-fc" rows="3" value={ssoConfig.idp_certificate || ""} placeholder="Paste SAP IAS public certificate here..."
                      onChange={(e) => setSsoConfig({ ...ssoConfig, idp_certificate: e.target.value })} style={{ fontFamily: "monospace", fontSize: 11 }} />
                  </div>
                  <div className="t2t-fg">
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" checked={!!ssoConfig.auto_provision}
                        onChange={(e) => setSsoConfig({ ...ssoConfig, auto_provision: e.target.checked })} />
                      Auto-provision users from SAML attributes
                    </label>
                  </div>
                  <div style={{ borderTop: "1px solid var(--t2t-border)", paddingTop: 12, marginTop: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Fiori Launchpad Embedding</div>
                    <div className="t2t-fg">
                      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input type="checkbox" checked={!!ssoConfig.fiori_embed_enabled}
                          onChange={(e) => setSsoConfig({ ...ssoConfig, fiori_embed_enabled: e.target.checked })} />
                        Enable Fiori iframe embed mode
                      </label>
                    </div>
                    {ssoConfig.fiori_embed_enabled && (
                      <div className="t2t-fg">
                        <label>Allowed Fiori Origin URL</label>
                        <input className="t2t-fc" value={ssoConfig.fiori_origin || ""} placeholder="https://fiori.yourcompany.com"
                          onChange={(e) => setSsoConfig({ ...ssoConfig, fiori_origin: e.target.value })} />
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="t2t-fg">
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={!!ssoConfig.is_active}
                    onChange={(e) => setSsoConfig({ ...ssoConfig, is_active: e.target.checked })} />
                  Active (enable SSO login)
                </label>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={saveSsoConfig} disabled={ssoSaving}>
                  {ssoSaving ? "Saving..." : "Save Config"}
                </button>
                {ssoConfig.sso_type !== "none" && (
                  <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={testSsoConnection} disabled={ssoTesting}>
                    {ssoTesting ? "Testing..." : "Test IdP Connection"}
                  </button>
                )}
              </div>
              {ssoTestResult && (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                  background: ssoTestResult.ok ? "#F0FDF4" : "#FEF2F2",
                  color: ssoTestResult.ok ? "#166534" : "#DC2626",
                  border: `1px solid ${ssoTestResult.ok ? "#BBF7D0" : "#FECACA"}` }}>
                  {ssoTestResult.ok ? "✅ " : "❌ "}{ssoTestResult.msg}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ---- ROLE MODAL ---- */}
      {roleModal !== null && (
        <div className="t2t-overlay" onClick={() => setRoleModal(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div className="t2t-modal-title">{roleModal === "new" ? "Add Role" : "Edit Role"}</div>
              <button className="t2t-close-btn" onClick={() => setRoleModal(null)}>×</button>
            </div>
            <div className="t2t-modal-body">
              {roleFormError && <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{roleFormError}</div>}

              <div className="t2t-fg">
                <label>Role Name *</label>
                <input className="t2t-fc" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="e.g. Finance Controller" />
              </div>

              <div className="t2t-fg">
                <label>Color</label>
                <div className="t2t-color-picker-row">
                  {COLORS.map((c) => (
                    <div key={c} className={`t2t-color-swatch${roleForm.color === c ? " selected" : ""}`} style={{ background: c }} onClick={() => setRoleForm({ ...roleForm, color: c })} />
                  ))}
                </div>
              </div>

              <div className="t2t-fg">
                <label>Description</label>
                <input className="t2t-fc" value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} placeholder="Brief description..." />
              </div>

              <div className="t2t-fg">
                <label>Org Unit Scope *</label>
                <select className="t2t-fc" value={roleForm.org_unit_id} onChange={(e) => setRoleForm({ ...roleForm, org_unit_id: e.target.value })}>
                  <option value="">All Org Units</option>
                  {orgUnits.filter((ou) => ou.type === "Group").map((ou) => (
                    <option key={ou.id} value={ou.id}>{ou.name}</option>
                  ))}
                </select>
              </div>

              {/* Permission Flags */}
              <div style={{ borderTop: "1px dashed var(--t2t-border)", paddingTop: 14, marginTop: 6 }}>
                <label style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, display: "block" }}>Permissions</label>
                {PERMISSION_GROUPS.map((group) => (
                  <div key={group.label} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                      {group.label}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
                      {group.flags.map((flag) => (
                        <label key={flag.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={!!roleForm.permissions[flag.key]}
                            onChange={() =>
                              setRoleForm((prev) => ({
                                ...prev,
                                permissions: { ...prev.permissions, [flag.key]: !prev.permissions[flag.key] },
                              }))
                            }
                          />
                          {flag.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setRoleModal(null)}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary" onClick={saveRole} disabled={roleSaving}>
                {roleSaving ? "Saving..." : "Save Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- USER MODAL ---- */}
      {userModal !== null && (
        <div className="t2t-overlay" onClick={() => setUserModal(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div className="t2t-modal-title">{userModal === "new" ? "Add User" : "Edit User"}</div>
              <button className="t2t-close-btn" onClick={() => setUserModal(null)}>×</button>
            </div>
            <div className="t2t-modal-body">
              {userFormError && <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{userFormError}</div>}
              <div className="t2t-fg"><label>Full Name *</label><input className="t2t-fc" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="e.g. Ravi Kumar" /></div>
              <div className="t2t-fg"><label>Email *</label><input className="t2t-fc" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="ravi@company.com" /></div>
              <div className="t2t-fg"><label>Mobile Number *</label><input className="t2t-fc" type="tel" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} placeholder="e.g. +91 9876543210" /></div>
              {userModal === "new" && (
                <div className="t2t-fg"><label>Password *</label><input className="t2t-fc" type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Enter password" /></div>
              )}
              <div className="t2t-fg">
                <label>Role</label>
                <select className="t2t-fc" value={userForm.t2t_role_id} onChange={(e) => setUserForm({ ...userForm, t2t_role_id: e.target.value })}>
                  <option value="">Select role...</option>
                  {roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
                </select>
              </div>

              {/* Cascading org dropdowns */}
              <div className="t2t-fg">
                <label>Group *</label>
                <select className="t2t-fc" value={userForm.group_id}
                  onChange={(e) => setUserForm({ ...userForm, group_id: e.target.value, company_id: "", division_id: "", department_id: "" })}>
                  <option value="">Select Group...</option>
                  {orgUnits.filter((o) => o.type === "Group").map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Company *</label>
                <select className="t2t-fc" value={userForm.company_id} disabled={!userForm.group_id}
                  onChange={(e) => setUserForm({ ...userForm, company_id: e.target.value, division_id: "", department_id: "" })}>
                  <option value="">Select Company...</option>
                  {orgUnits.filter((o) => o.type === "Company" && String(o.parent_id) === String(userForm.group_id)).map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Division *</label>
                <select className="t2t-fc" value={userForm.division_id} disabled={!userForm.company_id}
                  onChange={(e) => setUserForm({ ...userForm, division_id: e.target.value, department_id: "" })}>
                  <option value="">Select Division...</option>
                  {orgUnits.filter((o) => o.type === "Division" && String(o.parent_id) === String(userForm.company_id)).map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Department *</label>
                <select className="t2t-fc" value={userForm.department_id} disabled={!userForm.division_id}
                  onChange={(e) => setUserForm({ ...userForm, department_id: e.target.value })}>
                  <option value="">Select Department...</option>
                  {orgUnits.filter((o) => o.type === "Department" && String(o.parent_id) === String(userForm.division_id)).map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Job Title *</label>
                <input className="t2t-fc" value={userForm.job_title} onChange={(e) => setUserForm({ ...userForm, job_title: e.target.value })} placeholder="e.g. Senior Analyst" />
              </div>

              {/* Reporting Manager — Step 1: Pick RO Role */}
              <div className="t2t-fg">
                <label>Reporting Manager Role</label>
                <select
                  className="t2t-fc"
                  value={userForm.ro_role_id}
                  disabled={!userForm.group_id || !userForm.company_id}
                  onChange={(e) => setUserForm({ ...userForm, ro_role_id: e.target.value, reporting_officer_id: "" })}
                >
                  <option value="">{!userForm.group_id ? "Select group & company first..." : "Select RM role..."}</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>{r.name}</option>
                  ))}
                </select>
              </div>

              {/* Reporting Manager — Step 2: Pick single user from that role */}
              {userForm.ro_role_id && (
                <div className="t2t-fg">
                  <label>Reporting Manager</label>
                  <select
                    className="t2t-fc"
                    value={userForm.reporting_officer_id}
                    onChange={(e) => setUserForm({ ...userForm, reporting_officer_id: e.target.value })}
                  >
                    <option value="">Select reporting manager...</option>
                    {users.filter((u) => {
                      if (String(u.user_id) === String(userModal)) return false;
                      if (String(u.t2t_role_id) !== String(userForm.ro_role_id)) return false;
                      if (userForm.group_id && u.t2t_group_id && String(u.t2t_group_id) !== String(userForm.group_id)) return false;
                      if (userForm.company_id && u.t2t_company_id && String(u.t2t_company_id) !== String(userForm.company_id)) return false;
                      return true;
                    }).map((u) => (
                      <option key={u.user_id} value={u.user_id}>{u.email} ({u.name})</option>
                    ))}
                  </select>
                </div>
              )}

              {userModal !== "new" && (
                <>
                  <div className="t2t-fg">
                    <label>Status</label>
                    <select className="t2t-fc" value={userForm.status} onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}>
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>

                  <div style={{ borderTop: "1px dashed var(--t2t-border)", paddingTop: 12, marginTop: 4 }}>
                    {pwdSuccess && (
                      <div style={{ padding: "8px 12px", background: "#F0FDF4", color: "#166534", borderRadius: 6, fontSize: 12, marginBottom: 8 }}>
                        ✓ Password updated successfully.
                      </div>
                    )}
                    <button
                      type="button"
                      className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        setShowPwdChange((p) => !p);
                        setPwdError("");
                        setPwdSuccess(false);
                        setUserForm((f) => ({ ...f, newPassword: "", confirmPassword: "" }));
                      }}
                    >
                      🔑 {showPwdChange ? "Cancel" : "Change Password"}
                    </button>

                    {showPwdChange && (
                      <div style={{ marginTop: 10 }}>
                        {pwdError && (
                          <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{pwdError}</div>
                        )}
                        <div className="t2t-fg">
                          <label>New Password *</label>
                          <input
                            className="t2t-fc"
                            type="password"
                            value={userForm.newPassword || ""}
                            onChange={(e) => setUserForm({ ...userForm, newPassword: e.target.value })}
                            placeholder="Min. 8 characters, include a number"
                          />
                        </div>
                        <div className="t2t-fg">
                          <label>Confirm Password *</label>
                          <input
                            className="t2t-fc"
                            type="password"
                            value={userForm.confirmPassword || ""}
                            onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                            placeholder="Re-enter new password"
                          />
                        </div>
                        <button
                          type="button"
                          className="t2t-btn t2t-btn-primary t2t-btn-sm"
                          style={{ fontSize: 12 }}
                          onClick={changePassword}
                          disabled={pwdSaving}
                        >
                          {pwdSaving ? "Updating..." : "🔑 Update Password"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setUserModal(null)}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary" onClick={saveUser} disabled={userSaving}>
                {userSaving ? "Saving..." : "Save User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- BULK IMPORT MODAL ---- */}
      {showImport && (
        <div className="t2t-overlay" onClick={() => setShowImport(false)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">Import Users from CSV</div>
                <div className="t2t-modal-sub">Upload a CSV file to create multiple users at once</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setShowImport(false)}>×</button>
            </div>
            <div className="t2t-modal-body" style={{ maxHeight: 450, overflowY: "auto" }}>

              {/* Step 1: Download sample */}
              <div style={{ padding: "12px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, marginBottom: 14, fontSize: 12, color: "#1E40AF" }}>
                <strong>Step 1:</strong> Download the sample CSV, fill in your users, then upload it below.
                <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ marginLeft: 10, fontSize: 11 }} onClick={downloadSampleCsv}>
                  ↓ Download Sample CSV
                </button>
              </div>

              {/* Step 2: Upload */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 700, fontSize: 12, display: "block", marginBottom: 6 }}>Step 2: Upload CSV</label>
                <div
                  style={{ border: "2px dashed var(--t2t-border)", borderRadius: 8, padding: 20, textAlign: "center", cursor: "pointer", background: importFile ? "#F0FDF4" : "#FAFBFC" }}
                  onClick={() => document.getElementById("import-csv-input")?.click()}
                >
                  {importFile ? (
                    <div style={{ fontSize: 13, color: "#16A34A", fontWeight: 600 }}>📄 {importFile.name} — {importPreview.length} user(s) found</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>📄</div>
                      <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>Click to select CSV file</div>
                    </>
                  )}
                  <input
                    id="import-csv-input"
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={(e) => { handleImportFile(e.target.files[0]); e.target.value = ""; }}
                  />
                </div>
              </div>

              {/* Validation errors */}
              {importErrors.length > 0 && (
                <div style={{ padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#DC2626", marginBottom: 6 }}>Validation Issues ({importErrors.length})</div>
                  {importErrors.slice(0, 10).map((err, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#DC2626", marginBottom: 2 }}>• {err}</div>
                  ))}
                  {importErrors.length > 10 && <div style={{ fontSize: 11, color: "#DC2626" }}>...and {importErrors.length - 10} more</div>}
                </div>
              )}

              {/* Preview table */}
              {importPreview.length > 0 && importErrors.length === 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Preview ({importPreview.length} users)</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#F8FAFC" }}>
                          {["#", "Name", "Email", "Phone", "Role", "Group", "Company", "Job Title", "Reporting Manager"].map((h) => (
                            <th key={h} style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #E2E8F0", fontWeight: 700, color: "#64748B" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.slice(0, 15).map((row, i) => (
                          <tr key={i}>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9", color: "#94A3B8" }}>{i + 1}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9", fontWeight: 600 }}>{row.name}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.email}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.phone || "—"}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.role_name || "—"}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.group || "—"}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.company || "—"}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.job_title || "—"}</td>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #F1F5F9" }}>{row.reporting_manager_email || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importPreview.length > 15 && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Showing first 15 of {importPreview.length}</div>}
                  </div>
                </div>
              )}

              {/* Import result */}
              {importResult && (
                <div style={{ padding: "12px 14px", borderRadius: 8, marginBottom: 14,
                  background: importResult.failed === 0 ? "#F0FDF4" : "#FFFBEB",
                  border: `1px solid ${importResult.failed === 0 ? "#BBF7D0" : "#FDE68A"}` }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                    {importResult.failed === 0 ? "✅ All users imported!" : `⚠️ ${importResult.success} imported, ${importResult.failed} failed`}
                  </div>
                  {importResult.success > 0 && <div style={{ fontSize: 12, color: "#16A34A" }}>{importResult.success} user(s) created successfully.</div>}
                  {importResult.failedRows.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      {importResult.failedRows.map((f, i) => (
                        <div key={i} style={{ fontSize: 11, color: "#DC2626" }}>Row {f.row}: {f.name} — {f.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* CSV format info */}
              <div style={{ fontSize: 11, color: "#94A3B8", lineHeight: 1.6 }}>
                <strong>CSV columns:</strong> name*, email*, password*, phone*, role_name, group, company, division, department, job_title, reporting_manager_email
                <br />Role, Group, Company, Division, Department must match existing names exactly (case-insensitive).
                <br />Reporting manager email (optional) must match an existing user&apos;s email; leave blank for no reporting manager.
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setShowImport(false)}>Close</button>
              <button
                className="t2t-btn t2t-btn-primary"
                onClick={executeImport}
                disabled={importing || importPreview.length === 0 || importErrors.length > 0}
              >
                {importing ? `Importing ${importPreview.length} users...` : `Import ${importPreview.length} User${importPreview.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
        {/* ---- GUIDANCE ANALYTICS ---- */}
        {activeTab === "guidance" && (
          <>
            {guidanceData.loading ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading analytics...</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Overview Cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  {[
                    { label: "Welcome Shown", value: guidanceData.overview?.welcome_shown ?? 0, color: "#2563EB" },
                    { label: "Wizard Started", value: guidanceData.overview?.wizard_started ?? 0, color: "#7C3AED" },
                    { label: "Wizard Completed", value: guidanceData.overview?.wizard_completed ?? 0, color: "#16A34A" },
                    { label: "Tutorial Started", value: guidanceData.overview?.tutorial_started ?? 0, color: "#D97706" },
                    { label: "Tutorial Completed", value: guidanceData.overview?.tutorial_completed ?? 0, color: "#16A34A" },
                    { label: "Templates Used", value: guidanceData.overview?.templates_used ?? 0, color: "#0F766E" },
                  ].map((card) => (
                    <div key={card.label} style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{card.label}</div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: card.color, marginTop: 4 }}>{card.value}</div>
                    </div>
                  ))}
                </div>

                {/* Wizard Drop-off */}
                <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Wizard Step Drop-off</div>
                  <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 12 }}>Which step do users most often abandon the wizard at?</p>
                  {Array.isArray(guidanceData.dropOff) && guidanceData.dropOff.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {guidanceData.dropOff.map((d, i) => {
                        const maxCount = Math.max(...guidanceData.dropOff.map((x) => x.count || 0), 1);
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, width: 60, flexShrink: 0 }}>Step {d.step || i + 1}</span>
                            <div style={{ flex: 1, height: 20, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${((d.count || 0) / maxCount) * 100}%`, background: d.step >= 5 ? "#DC2626" : d.step >= 3 ? "#D97706" : "#16A34A", borderRadius: 4, transition: "width 0.3s" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, width: 40, textAlign: "right" }}>{d.count || 0}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>No drop-off data yet.</div>
                  )}
                </div>

                {/* Hint Dismissals */}
                <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Most Dismissed Hints</div>
                  <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 12 }}>Which hints are dismissed most often? High dismissal = hint is clear. Low = users may not reach that feature.</p>
                  {Array.isArray(guidanceData.hints) && guidanceData.hints.length > 0 ? (
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #E2E8F0" }}>
                          <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 700 }}>Hint ID</th>
                          <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>Dismissed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {guidanceData.hints.map((h, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                            <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 11 }}>{h.hint_id}</td>
                            <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600 }}>{h.count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>No hint data yet.</div>
                  )}
                </div>

                {/* Template Usage */}
                <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Template Usage</div>
                  <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 12 }}>Which templates are most selected by users?</p>
                  {Array.isArray(guidanceData.templates) && guidanceData.templates.length > 0 ? (
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #E2E8F0" }}>
                          <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 700 }}>Template</th>
                          <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>Used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {guidanceData.templates.map((t, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                            <td style={{ padding: "6px 8px" }}>{t.template_name || t.name || t.template_id}</td>
                            <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600 }}>{t.count || t.usage_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>No template usage data yet.</div>
                  )}
                </div>

              </div>
            )}
          </>
        )}
    </>
  );
};

export default T2TAdmin;
